import { motion } from "framer-motion";
import { BookOpen, Target, TrendingUp, Award } from "lucide-react";

export default function JourneySection() {
  const weeks = [
    {
      number: "1",
      title: "الأسبوع الأول",
      subtitle: "التأسيس",
      icon: BookOpen,
      description: "فهم أساسيات التداول والمصطلحات الرئيسية",
      color: "#CFAE58",
    },
    {
      number: "2",
      title: "الأسبوع الثاني",
      subtitle: "الاستراتيجيات",
      icon: Target,
      description: "تعلم استراتيجيات التداول الفعالة والمجربة",
      color: "#CFAE58",
    },
    {
      number: "3",
      title: "الأسبوع الثالث",
      subtitle: "التطبيق العملي",
      icon: TrendingUp,
      description: "تطبيق ما تعلمته على حساب تجريبي",
      color: "#CFAE58",
    },
    {
      number: "4",
      title: "الأسبوع الرابع",
      subtitle: "المراجعة والجوائز",
      icon: Award,
      description: "تقييم الأداء والحصول على الجوائز",
      color: "#CFAE58",
    },
  ];

  return (
    <section className="relative py-24 bg-white overflow-hidden">

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Title */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-[#0A1E3F] mb-4">
            كيف رح تكون <span className="text-[#CFAE58]">رحلتك معنا</span> خلال 30 يوم؟
          </h2>
        </motion.div>

        {/* Journey Timeline */}
        <div className="max-w-7xl mx-auto">
          {/* Desktop Layout */}
          <div className="hidden lg:grid lg:grid-cols-4 gap-6 mb-12">
            {weeks.map((week, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="relative"
              >
                {/* Connecting Line */}
                {index < weeks.length - 1 && (
                  <div className="absolute top-16 left-full w-full h-1 bg-gradient-to-r from-[#CFAE58] to-[#CFAE58]/30 z-0 hidden lg:block"></div>
                )}

                <div className="relative z-10 bg-gradient-to-br from-[#0A1E3F]/5 to-white border-2 border-[#CFAE58]/30 rounded-2xl p-6 hover:border-[#CFAE58] transition-all duration-300 hover:shadow-2xl hover:shadow-[#CFAE58]/20 transform hover:scale-105 h-full">
                  {/* Number Badge */}
                  <div className="absolute -top-6 right-6 bg-gradient-to-br from-[#CFAE58] to-[#B89A45] text-white w-12 h-12 rounded-full flex items-center justify-center shadow-lg text-xl">
                    {week.number}
                  </div>

                  {/* Icon */}
                  <div className="mt-4 mb-4 flex justify-center">
                    <div className="bg-gradient-to-br from-[#CFAE58]/20 to-[#CFAE58]/10 p-4 rounded-full">
                      <week.icon className="w-8 h-8 text-[#CFAE58]" />
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl text-[#0A1E3F] mb-2 text-center">{week.title}</h3>

                  {/* Subtitle */}
                  <div className="text-2xl text-[#CFAE58] mb-3 text-center">
                    {week.subtitle}
                  </div>

                  {/* Description */}
                  <p className="text-gray-600 text-center text-sm leading-relaxed">
                    {week.description}
                  </p>

                  {/* Decorative Corner */}
                  <div className="absolute bottom-3 left-3 w-6 h-6 border-b-2 border-l-2 border-[#CFAE58]/30 rounded-bl-lg"></div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Mobile/Tablet Layout */}
          <div className="lg:hidden space-y-6 mb-12">
            {weeks.map((week, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="relative"
              >
                <div className="bg-gradient-to-br from-[#0A1E3F]/5 to-white border-2 border-[#CFAE58]/30 rounded-2xl p-6 hover:border-[#CFAE58] transition-all duration-300">
                  {/* Number Badge */}
                  <div className="absolute -top-4 -right-4 bg-gradient-to-br from-[#CFAE58] to-[#B89A45] text-white w-12 h-12 rounded-full flex items-center justify-center shadow-lg text-xl">
                    {week.number}
                  </div>

                  <div className="flex items-center gap-4">
                    {/* Icon */}
                    <div className="bg-gradient-to-br from-[#CFAE58]/20 to-[#CFAE58]/10 p-4 rounded-full flex-shrink-0">
                      <week.icon className="w-8 h-8 text-[#CFAE58]" />
                    </div>

                    <div className="flex-1">
                      {/* Title */}
                      <h3 className="text-lg text-[#0A1E3F] mb-1">{week.title}</h3>

                      {/* Subtitle */}
                      <div className="text-xl text-[#CFAE58] mb-2">{week.subtitle}</div>

                      {/* Description */}
                      <p className="text-gray-600 text-sm">{week.description}</p>
                    </div>
                  </div>
                </div>

                {/* Connecting Line for Mobile */}
                {index < weeks.length - 1 && (
                  <div className="w-1 h-6 bg-gradient-to-b from-[#CFAE58] to-[#CFAE58]/30 mx-auto"></div>
                )}
              </motion.div>
            ))}
          </div>

          {/* Bottom Message */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="bg-gradient-to-r from-[#CFAE58]/10 via-[#CFAE58]/20 to-[#CFAE58]/10 border border-[#CFAE58]/30 rounded-full px-8 py-4 inline-block">
              <p className="text-xl text-[#0A1E3F]">
                بنهاية التحدي، رح تكون جاهز تبدأ التداول <span className="text-[#CFAE58]">بثقة واستقلالية</span>.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
